import React, { useEffect, useRef, useState, useCallback, useContext } from "react";
import axios from "../../../../Axios";
import { Urls } from "../../../../Urls";
import Loader from "../../../Loader";
import { toast } from "react-toastify";
import styles from "./ClarificationThread.module.css";
import { MyCompanyProfileContext } from "../../../../MyCompanyProfileContext";
import { useMixPanelForTracking } from "../../../../utils/TrackMixPanel";

const POLL_INTERVAL_MS = 15000;

const formatTimestamp = (ts) => {
  if (!ts) return "";
  const date = new Date(ts);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Per-transporter clarification thread between adjacent approval levels.
 * Ln may only initiate ("clarification_needed") to L(n-1); the recipient
 * replies with "clarify". L0 (the enquiry creator) can only reply, never
 * initiate, since there is no level below L0.
 */
const ClarificationThread = ({
  enquiryId,
  forTransporterId,
  currentUserEmail,
  currentUserName,
  currentUserLevel,
  onClose,
}) => {
  const { companySettingData } = useContext(MyCompanyProfileContext);
  const { TrackMixPanel } = useMixPanelForTracking(
    { email: currentUserEmail, user_name: currentUserName },
    companySettingData
  );

  const [messages, setMessages] = useState([]);
  const [show, setShow] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (enquiryId && forTransporterId) {
      TrackMixPanel({
        ModuleName: "lorri spot",
        ScreenName: "my enquiries - clarification chat",
        Data: `enquiryId: ${enquiryId}, transporterId: ${forTransporterId}`,
        Definition: "user viewed clarification chat thread",
      });
    }
  }, [enquiryId, forTransporterId]);

  const fetchMessages = useCallback(
    (showLoader = false) => {
      if (!enquiryId) return;
      if (showLoader) setShow(true);
      axios
        .get(`${Urls.spot}?field=get_clarification&enquiry_id=${enquiryId}`)
        .then((res) => {
          const all = Array.isArray(res.data) ? res.data : [];
          const scoped = all.filter(
            (m) => m.for_transporter_id === forTransporterId
          );
          setMessages(scoped);
        })
        .catch(() => {})
        .finally(() => {
          if (showLoader) setShow(false);
        });
    },
    [enquiryId, forTransporterId]
  );

  const markRead = useCallback(
    (latestMessages) => {
      if (!latestMessages.length || !currentUserEmail) return;
      const lastMessageObj = latestMessages[latestMessages.length - 1];
      if (lastMessageObj?.email === currentUserEmail) return;
      const lastSeen = lastMessageObj?.message_timestamp;
      if (!lastSeen) return;
      axios
        .post(`${Urls.spot}?field=clarification_read_status`, {
          enquiry_id: enquiryId,
          for_transporter_id: forTransporterId,
          read_by_email: currentUserEmail,
          level: currentUserLevel,
          last_seen_message_timestamp: lastSeen,
        })
        .catch(() => {});
    },
    [enquiryId, forTransporterId, currentUserEmail, currentUserLevel]
  );

  useEffect(() => {
    fetchMessages(true);
  }, [fetchMessages]);

  useEffect(() => {
    if (messages.length > 0) {
      markRead(messages);
      bottomRef.current?.scrollIntoView({ block: "nearest" });
    }
  }, [messages, markRead]);

  const lastMessage = messages[messages.length - 1];
  const isReply =
    lastMessage &&
    lastMessage.type === "clarification_needed" &&
    lastMessage.level === currentUserLevel + 1;

  const canInitiate = currentUserLevel > 0;
  const sendType = isReply ? "clarify" : "clarification_needed";
  const canSend = isReply || canInitiate;

  const handleSend = () => {
    const trimmed = messageText.trim();
    if (!trimmed || !canSend) return;

    setSending(true);
    axios
      .post(`${Urls.spot}?field=clarification`, {
        type: sendType,
        email: currentUserEmail,
        level: currentUserLevel,
        message: trimmed,
        for_transporter_id: forTransporterId,
        enquiry_id: enquiryId,
      })
      .then((res) => {
        if (res.data?.status === false) {
          toast.error(res.data?.error || "Failed to send message");
          return;
        }
        TrackMixPanel({
          ModuleName: "lorri spot",
          ScreenName: "my enquiries - clarification chat",
          Data: `enquiryId: ${enquiryId}, transporterId: ${forTransporterId}, type: ${sendType}`,
          Definition: "user sent clarification message",
        });
        setMessageText("");
        fetchMessages(false);
      })
      .catch(() => {
        toast.error("Failed to send message");
      })
      .finally(() => {
        setSending(false);
      });
  };

  return (
    <div className={styles.wrapper}>
      <Loader show={show} />
      <div className={styles.messageList}>
        {messages.length === 0 && !show && (
          <div className={styles.emptyState}>No clarification messages yet.</div>
        )}
        {messages.map((msg, index) => {
          const isOwn = msg.email === currentUserEmail;
          return (
            <div
              key={`${msg.email}-${msg.message_timestamp}-${index}`}
              className={`${styles.messageRow} ${isOwn ? styles.messageRowOwn : ""}`}
            >
              <div className={`${styles.bubble} ${isOwn ? styles.bubbleOwn : ""}`}>
                <div className={styles.bubbleMeta}>
                  <span className={styles.bubbleName}>{msg.name || msg.email}</span>
                  <span className={styles.bubbleLevel}>L{msg.level}</span>
                </div>
                <div className={styles.bubbleText}>{msg.message}</div>
                <div className={styles.bubbleTime}>{formatTimestamp(msg.message_timestamp)}</div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <div className={styles.composer}>
        {canSend ? (
          <>
            <textarea
              className={styles.textarea}
              style={{
                  height: '36px',
                  minHeight: '36px',
                  maxHeight: '36px',
                  borderRadius: '20px',
                  border: '1px solid #cbd5e1',
                  padding: '8px 16px',
                  resize: 'none',
                  fontSize: '13px',
              }}
              rows={1}
              placeholder={
                isReply ? "Type your clarification..." : "Ask for clarification..."
              }
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              disabled={sending}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
            />
            <button
              type="button"
              className={styles.sendButton}
              onClick={handleSend}
              disabled={sending || !messageText.trim()}
            >
              <i className="fa fa-paper-plane" />
            </button>
          </>
        ) : (
          <div className={styles.composerDisabled}>
            You can only reply once a clarification has been raised to you.
          </div>
        )}
      </div>
    </div>
  );
};

export default ClarificationThread;
